// Utility functions for chart data analysis and configuration
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

// Color palette for charts
export const CHART_COLORS = {
  primary: '#8B4513', // Brown (Got Tea brand color)
  secondary: '#D2691E',
  success: '#28a745',
  warning: '#ffc107',
  danger: '#dc3545',
  info: '#17a2b8',
  light: '#f8f9fa',
  dark: '#343a40',
  gradients: [
    'rgba(139, 69, 19, 0.8)',   // Brown
    'rgba(210, 105, 30, 0.8)',  // Chocolate
    'rgba(160, 82, 45, 0.8)',   // Saddle Brown
    'rgba(205, 133, 63, 0.8)',  // Peru
    'rgba(222, 184, 135, 0.8)', // Burlywood
    'rgba(188, 143, 143, 0.8)', // Rosy Brown
    'rgba(176, 196, 222, 0.8)', // Light Steel Blue
    'rgba(147, 112, 219, 0.8)', // Medium Purple
  ]
};

// Analyze data structure and determine optimal chart type
export const analyzeDataForChart = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return { chartType: 'none', reason: 'No data available' };
  }

  const firstRow = data[0];
  const columns = Object.keys(firstRow);
  const numColumns = columns.length;
  const numRows = data.length;

  // Helper functions
  const isNumericColumn = (columnName) => {
    return data.every(row => {
      const value = row[columnName];
      return value === null || value === undefined || !isNaN(Number(value));
    });
  };

  const isDateColumn = (columnName) => {
    const name = columnName.toLowerCase();
    return name.includes('date') || name.includes('time') || name.includes('created') || name.includes('updated');
  };

  const isCurrencyColumn = (columnName) => {
    const name = columnName.toLowerCase();
    const currencyKeywords = ['price', 'amount', 'revenue', 'spent', 'sales', 'sale', 'cost', 'spend', 'total'];
    return currencyKeywords.some(keyword => name.includes(keyword));
  };

  // Find numeric columns
  const numericColumns = columns.filter(isNumericColumn);
  const dateColumns = columns.filter(isDateColumn);
  const categoryColumns = columns.filter(col => !isNumericColumn(col) && !isDateColumn(col));

  // Chart type logic
  if (numColumns === 2 && numericColumns.length === 1 && categoryColumns.length === 1) {
    // Perfect for bar/pie chart (category + value)
    if (numRows <= 8) {
      return {
        chartType: 'pie',
        reason: 'Small dataset with categories and values - perfect for pie chart',
        labelColumn: categoryColumns[0],
        valueColumn: numericColumns[0]
      };
    } else {
      return {
        chartType: 'bar',
        reason: 'Categories with values - ideal for bar chart',
        labelColumn: categoryColumns[0],
        valueColumn: numericColumns[0]
      };
    }
  }

  if (dateColumns.length === 1 && numericColumns.length >= 1) {
    // Time series data - perfect for line chart
    return {
      chartType: 'line',
      reason: 'Time series data detected - line chart shows trends',
      xColumn: dateColumns[0],
      yColumns: numericColumns
    };
  }

  if (numericColumns.length >= 2 && categoryColumns.length === 1) {
    // Multiple metrics by category - grouped bar chart
    return {
      chartType: 'groupedBar',
      reason: 'Multiple metrics by category - grouped bar chart',
      labelColumn: categoryColumns[0],
      valueColumns: numericColumns
    };
  }

  if (numericColumns.length >= 2 && categoryColumns.length === 0) {
    // Correlation analysis - scatter plot
    return {
      chartType: 'scatter',
      reason: 'Multiple numeric values - scatter plot for correlation',
      xColumn: numericColumns[0],
      yColumn: numericColumns[1]
    };
  }

  if (numRows === 1 && numericColumns.length >= 2) {
    // Single row with multiple metrics - horizontal bar or radar
    return {
      chartType: 'horizontalBar',
      reason: 'Single entity with multiple metrics',
      metrics: numericColumns
    };
  }

  // Default fallback
  if (numericColumns.length > 0 && categoryColumns.length > 0) {
    return {
      chartType: 'bar',
      reason: 'Mixed data types - defaulting to bar chart',
      labelColumn: categoryColumns[0],
      valueColumn: numericColumns[0]
    };
  }

  return {
    chartType: 'table',
    reason: 'Data structure not suitable for charting'
  };
};

// Generate chart configuration based on analysis
export const generateChartConfig = (data, analysis) => {
  const { chartType, labelColumn, valueColumn, valueColumns, xColumn, yColumns } = analysis;

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#343a40',
          font: {
            family: 'BNBergen, sans-serif',
            size: 12
          }
        }
      },
      title: {
        display: false
      },
      datalabels: {
        display: false // We'll enable this selectively
      }
    },
    scales: {},
  };

  switch (chartType) {
    case 'pie':
      return {
        type: 'pie',
        data: {
          labels: data.map(row => row[labelColumn]),
          datasets: [{
            data: data.map(row => Number(row[valueColumn]) || 0),
            backgroundColor: CHART_COLORS.gradients.slice(0, data.length),
            borderColor: CHART_COLORS.primary,
            borderWidth: 2,
          }]
        },
        options: {
          ...baseOptions,
          plugins: {
            ...baseOptions.plugins,
            datalabels: {
              display: true,
              color: 'white',
              font: {
                weight: 'bold',
                size: 11
              },
              formatter: (value, context) => {
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return `${percentage}%`;
              }
            }
          }
        }
      };

    case 'bar':
      return {
        type: 'bar',
        data: {
          labels: data.map(row => row[labelColumn]),
          datasets: [{
            label: valueColumn.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            data: data.map(row => Number(row[valueColumn]) || 0),
            backgroundColor: CHART_COLORS.gradients[0],
            borderColor: CHART_COLORS.primary,
            borderWidth: 2,
          }]
        },
        options: {
          ...baseOptions,
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0,0,0,0.1)'
              },
              ticks: {
                color: '#343a40'
              }
            },
            x: {
              grid: {
                display: false
              },
              ticks: {
                color: '#343a40',
                maxRotation: 45
              }
            }
          }
        }
      };

    case 'line':
      const lineData = data.sort((a, b) => new Date(a[xColumn]) - new Date(b[xColumn]));
      return {
        type: 'line',
        data: {
          labels: lineData.map(row => {
            const date = new Date(row[xColumn]);
            return date.toLocaleDateString();
          }),
          datasets: yColumns.map((col, index) => ({
            label: col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            data: lineData.map(row => Number(row[col]) || 0),
            borderColor: CHART_COLORS.gradients[index % CHART_COLORS.gradients.length],
            backgroundColor: CHART_COLORS.gradients[index % CHART_COLORS.gradients.length].replace('0.8', '0.1'),
            borderWidth: 3,
            fill: false,
            tension: 0.2
          }))
        },
        options: {
          ...baseOptions,
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0,0,0,0.1)'
              },
              ticks: {
                color: '#343a40'
              }
            },
            x: {
              grid: {
                color: 'rgba(0,0,0,0.1)'
              },
              ticks: {
                color: '#343a40'
              }
            }
          }
        }
      };

    case 'groupedBar':
      return {
        type: 'bar',
        data: {
          labels: data.map(row => row[labelColumn]),
          datasets: valueColumns.map((col, index) => ({
            label: col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            data: data.map(row => Number(row[col]) || 0),
            backgroundColor: CHART_COLORS.gradients[index % CHART_COLORS.gradients.length],
            borderColor: CHART_COLORS.primary,
            borderWidth: 1,
          }))
        },
        options: {
          ...baseOptions,
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0,0,0,0.1)'
              },
              ticks: {
                color: '#343a40'
              }
            },
            x: {
              grid: {
                display: false
              },
              ticks: {
                color: '#343a40'
              }
            }
          }
        }
      };

    case 'horizontalBar':
      return {
        type: 'bar',
        data: {
          labels: analysis.metrics.map(col => col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())),
          datasets: [{
            label: 'Values',
            data: analysis.metrics.map(col => Number(data[0][col]) || 0),
            backgroundColor: CHART_COLORS.gradients.slice(0, analysis.metrics.length),
            borderColor: CHART_COLORS.primary,
            borderWidth: 2,
          }]
        },
        options: {
          ...baseOptions,
          indexAxis: 'y',
          scales: {
            x: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0,0,0,0.1)'
              },
              ticks: {
                color: '#343a40'
              }
            },
            y: {
              grid: {
                display: false
              },
              ticks: {
                color: '#343a40'
              }
            }
          }
        }
      };

    default:
      return null;
  }
};

// Format values for chart tooltips
export const formatTooltipValue = (value, columnName) => {
  const name = columnName?.toLowerCase() || '';
  const currencyKeywords = ['price', 'amount', 'revenue', 'spent', 'sales', 'sale', 'cost', 'spend', 'total'];
  
  if (currencyKeywords.some(keyword => name.includes(keyword))) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }
  
  if (typeof value === 'number') {
    return value.toLocaleString();
  }
  
  return value;
}; 